import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import {
  Tabs,
  Tag,
  Tooltip,
  Spin,
  Empty,
  Badge,
  Table,
  Select,
  Input,
  Dropdown,
  Pagination,
} from "antd";
// jspdf loaded dynamically in generatePDF to avoid global CSS side effects
import {
  FileProtectOutlined,
  AlertOutlined,
  CalendarOutlined,
  UserOutlined,
  AuditOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  FireOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  GlobalOutlined,
  BookOutlined,
  DownloadOutlined,
  SyncOutlined,
  ReloadOutlined,
  /* The Account tab's own icon. The eight panel icons live with the panels, in
     AccountTabs, rather than being imported here for a bar that no longer
     renders them. */
  WalletOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  AreaChart,
  Area,
} from "recharts";
import {
  fetchDashboardStatus,
  fetchPortfolioHearings,
  fetchAllDeadlines,
  fetchAttorneyFilings,
  fetchInboundOppositions,
  fetchOutboundOppositions,
  fetchJournalResults,
  fetchPreJournalData,
  fetchMasterJournalData,
} from "../../services/user360Service";
import { fetchAttorneyRenewalData } from "../../services/user360Service";
import { fetchCustomerSummary } from "../../services/customer360Service";
import AccountSummaryCards from "./customer360/AccountSummaryCards";
import AccountTabs from "./customer360/AccountTabs";

const { Option } = Select;

// ─── Color palettes ──────────────────────────────────────────────────────────
const STATUS_COLORS = {
  "Formalities Chk Pass": "#3b82f6",
  Registered: "#10b981",
  Objected: "#f59e0b",
  Abandoned: "#6b7280",
  Refused: "#ef4444",
  "Accepted & Advertised": "#8b5cf6",
  Opposed: "#f97316",
  "Send To Vienna Codification": "#06b6d4",
  "Formalities Chk Fail": "#dc2626",
  "Protection Granted": "#059669",
  Withdrawn: "#9ca3af",
  Accepted: "#7c3aed",
  "Marked for Exam": "#0ea5e9",
  "Rectification Filed": "#a855f7",
  Cancelled: "#be123c",
  "Deemed to be Abandoned": "#78716c",
  "Exam Report Issued": "#0891b2",
  default: "#94a3b8",
};

const PIE_COLORS = [
  "#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#f97316", "#ec4899", "#14b8a6", "#a855f7",
  "#64748b", "#d946ef",
];

const URGENCY_CONFIG = {
  OVERDUE: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Overdue" },
  URGENT: { color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", label: "Urgent" },
  NORMAL: { color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", label: "Normal" },
  LOW: { color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0", label: "Low" },
  NONE: { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", label: "None" },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n ?? 0).toLocaleString("en-IN");

const urgencyTag = (level) => {
  const c = URGENCY_CONFIG[level] || URGENCY_CONFIG.NONE;
  return (
    <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, color: c.color, background: c.bg, border: `1px solid ${c.border}`, whiteSpace: "nowrap" }}>
      {c.label}
    </span>
  );
};

const statusTag = (status) => {
  const color = STATUS_COLORS[status] || STATUS_COLORS.default;
  return (
    <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, color, background: `${color}18`, whiteSpace: "nowrap" }}>
      {status || "—"}
    </span>
  );
};

const tmLogo = (url, name) => {
  if (!url || url === "" || url === "None") {
    return (
      <div style={{ width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14 }}>
        {(name || "T")[0]?.toUpperCase()}
      </div>
    );
  }
  return <img src={url} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", border: "1px solid #e2e8f0" }} onError={(e) => { e.target.style.display = "none"; }} />;
};

const cardStyle = {
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)",
  border: "1px solid rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const sectionTitle = (icon, title, subtitle, iconBg) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 24px 0" }}>
    <div style={{ width: 38, height: 38, borderRadius: 10, background: iconBg || "linear-gradient(135deg,#6366f1,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 17, flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", letterSpacing: -0.3 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 1 }}>{subtitle}</div>}
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const User360View = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const userName = location.state?.userName || location.state?.user_name || "";

  // ── Overview data ──
  const [dashboard, setDashboard] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);

  /* Account-side summary for the Overview cards. Fetched with the overview
     rather than per tab — its numbers are what the tab badges read from, so they
     have to exist before a tab is opened. */
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  /* Which account panel a summary card pointed at, so the click lands on the
     figure it came from rather than on whichever panel happens to be first.
     Also seeded from navigation state, so a link elsewhere in the CMS can open
     this view straight on the panel it meant. */
  const [accountPanel, setAccountPanel] = useState(location.state?.openPanel || null);

  useEffect(() => {
    if (location.state?.openPanel) setActiveTab("account");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Tab data ──
  const [activeTab, setActiveTab] = useState("overview");

  // Trademarks
  const [filings, setFilings] = useState(null);
  const [filingsPage, setFilingsPage] = useState(1);
  const [filingsLoading, setFilingsLoading] = useState(false);
  const [filingsSearch, setFilingsSearch] = useState("");

  // Inbound oppositions
  const [inbound, setInbound] = useState(null);
  const [inboundPage, setInboundPage] = useState(1);
  const [inboundLoading, setInboundLoading] = useState(false);

  // Outbound oppositions
  const [outbound, setOutbound] = useState(null);
  const [outboundPage, setOutboundPage] = useState(1);
  const [outboundLoading, setOutboundLoading] = useState(false);

  // Journal
  const [journalCodes, setJournalCodes] = useState([]);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [journalResults, setJournalResults] = useState(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [preJournal, setPreJournal] = useState(null);
  const [preJournalLoading, setPreJournalLoading] = useState(false);

  // Renewals
  const [renewals, setRenewals] = useState(null);
  const [renewalsPage, setRenewalsPage] = useState(1);
  const [renewalsLoading, setRenewalsLoading] = useState(false);
  const [renewalsSearch, setRenewalsSearch] = useState("");
  const PAGE_LIMIT_RENEWALS = 50;

  // ── Load overview on mount ──
  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoadingOverview(true);
      try {
        const dashRes = await fetchDashboardStatus(id);
        setDashboard(dashRes?.dashboard || null);
      } catch (e) {
        console.error("360 dashboard load error:", e);
      } finally {
        setLoadingOverview(false);
      }
      /* Account summary, alongside the portfolio one. Its own try/catch because
         these tables are newer than the rest of the screen — an environment
         without them should leave the account cards empty, not take the whole
         Overview down with it. */
      try {
        const sumRes = await fetchCustomerSummary(id);
        setSummary(sumRes?.data || null);
      } catch (e) {
        console.error("360 summary load error:", e);
        setSummary(null);
      } finally {
        setSummaryLoading(false);
      }

      // Load secondary overview data in background (non-blocking)
      try {
        const [deadRes, hearRes] = await Promise.all([
          fetchAllDeadlines(id),
          fetchPortfolioHearings(id),
        ]);
        setDeadlines(deadRes || {});
        setHearings(hearRes?.data || []);
      } catch (e) {
        console.error("360 secondary load error:", e);
      }
    };
    load();
  }, [id]);

  // ── Tab-specific lazy loading ──
  const loadFilings = useCallback(async (page) => {
    setFilingsLoading(true);
    try {
      const res = await fetchAttorneyFilings(id, page, 20);
      setFilings(res);
      setFilingsPage(page);
    } catch { setFilings(null); }
    finally { setFilingsLoading(false); }
  }, [id]);

  const loadInbound = useCallback(async (page) => {
    setInboundLoading(true);
    try {
      const res = await fetchInboundOppositions(id, page, 20);
      setInbound(res);
      setInboundPage(page);
    } catch { setInbound(null); }
    finally { setInboundLoading(false); }
  }, [id]);

  const loadOutbound = useCallback(async (page) => {
    setOutboundLoading(true);
    try {
      const res = await fetchOutboundOppositions(id, page, 20);
      setOutbound(res);
      setOutboundPage(page);
    } catch { setOutbound(null); }
    finally { setOutboundLoading(false); }
  }, [id]);

  const loadJournalCodes = useCallback(async () => {
    try {
      const res = await fetchMasterJournalData();
      const codes = res?.data || [];
      setJournalCodes(codes);
      if (codes.length > 0 && !selectedJournal) {
        setSelectedJournal(codes[0].journal_number);
      }
    } catch { setJournalCodes([]); }
  }, [selectedJournal]);

  // Journal filters
  const [jPage, setJPage] = useState(1);
  const [jSameClass, setJSameClass] = useState(false);
  const [jMinSim, setJMinSim] = useState(50);
  const [jMatchTypes, setJMatchTypes] = useState([]);
  const [jSearch, setJSearch] = useState("");

  const jFilterParams = (overrides = {}) => {
    const f = { same_class: jSameClass || undefined, min_similarity: jMinSim || undefined, search: jSearch || undefined, match_types: jMatchTypes.length ? jMatchTypes.join(",") : undefined, ...overrides };
    Object.keys(f).forEach((k) => { if (f[k] === "" || f[k] === undefined || f[k] === null || f[k] === false) delete f[k]; });
    return f;
  };

  const loadJournalResults = useCallback(async (code, filters = {}) => {
    setJournalLoading(true);
    try {
      const res = await fetchJournalResults(id, code, filters);
      setJournalResults(res);
    } catch { setJournalResults(null); }
    finally { setJournalLoading(false); }
  }, [id]);

  const searchJournal = (overrides = {}) => {
    setJPage(1);
    if (selectedJournal) loadJournalResults(selectedJournal, jFilterParams(overrides));
  };

  const loadPreJournal = useCallback(async () => {
    setPreJournalLoading(true);
    try {
      const res = await fetchPreJournalData(id, 1, 20);
      setPreJournal(res);
    } catch { setPreJournal(null); }
    finally { setPreJournalLoading(false); }
  }, [id]);

  const loadRenewals = useCallback(async (page) => {
    setRenewalsLoading(true);
    try {
      const res = await fetchAttorneyRenewalData({ userId: id, page, limit: PAGE_LIMIT_RENEWALS });
      setRenewals(res);
      setRenewalsPage(page);
    } catch { setRenewals(null); }
    finally { setRenewalsLoading(false); }
  }, [id]);

  useEffect(() => {
    if (activeTab === "filings" && !filings) loadFilings(1);
    if (activeTab === "inbound" && !inbound) loadInbound(1);
    if (activeTab === "outbound" && !outbound) loadOutbound(1);
    if (activeTab === "journal" && journalCodes.length === 0) loadJournalCodes();
    if (activeTab === "prejournal" && !preJournal) loadPreJournal();
    if (activeTab === "renewals" && !renewals) loadRenewals(1);
  }, [activeTab]);

  useEffect(() => {
    if (selectedJournal) { setJPage(1); loadJournalResults(selectedJournal, jFilterParams()); }
  }, [selectedJournal]);

  // ── Derived data ──
  const db = dashboard || {};
  const topStatuses = useMemo(() => db.status_counts || [], [db.status_counts]);
  const topStates = useMemo(() => db.state_counts || [], [db.state_counts]);
  const topClasses = useMemo(() => {
    const raw = db.class_counts || [];
    const map = {};
    raw.forEach((c) => {
      const num = (c.class_number || "").replace(/\[.*\]/, "").trim();
      if (!num) return;
      map[num] = (map[num] || 0) + c.count;
    });
    return Object.entries(map)
      .map(([class_number, count]) => ({ class_number, count }))
      .sort((a, b) => Number(a.class_number) - Number(b.class_number));
  }, [db.class_counts]);
  const yearlyFilings = useMemo(() => (db.yearly_filings || []).slice(0, 8).reverse(), [db.yearly_filings]);

  const urgentDeadlines = useMemo(
    () => (deadlines?.data || []).filter((d) => d.urgency === "OVERDUE" || d.urgency === "URGENT").slice(0, 5),
    [deadlines?.data],
  );

  // group journals by month for selector
  const journalsByMonth = useMemo(() => {
    const map = {};
    journalCodes.forEach((j) => {
      const d = new Date(j.date_of_publication);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      if (!map[key]) map[key] = { label, journals: [] };
      map[key].journals.push(j);
    });
    return Object.values(map).slice(0, 5);
  }, [journalCodes]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER SECTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Shimmer ──
  const shimmerBg = "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)";
  const shimmerStyle = { backgroundSize: "200% 100%", animation: "shimmer360 1.4s infinite" };
  const shimmerBox = (w, h, r = 10) => <div style={{ width: w, height: h, borderRadius: r, background: shimmerBg, ...shimmerStyle }} />;

  const renderOverviewShimmer = () => (
    <div>
      <style>{`@keyframes shimmer360 { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
      {/* KPI shimmer */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 16, marginBottom: 24 }}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={{ ...cardStyle, padding: "22px 20px", display: "flex", alignItems: "center", gap: 16 }}>
            {shimmerBox(52, 52, 14)}
            <div style={{ flex: 1 }}>
              {shimmerBox("60%", 12, 6)}
              <div style={{ marginTop: 8 }}>{shimmerBox("40%", 28, 6)}</div>
            </div>
          </div>
        ))}
      </div>
      {/* Charts shimmer */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 24 }}>
        {[1, 2].map((i) => (
          <div key={i} style={{ ...cardStyle, flex: 1, minWidth: 320, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              {shimmerBox(38, 38, 10)}
              {shimmerBox(120, 16, 6)}
            </div>
            {shimmerBox("100%", 220, 8)}
          </div>
        ))}
      </div>
      {/* Bottom charts shimmer */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        {[1, 2].map((i) => (
          <div key={i} style={{ ...cardStyle, flex: 1, minWidth: 320, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              {shimmerBox(38, 38, 10)}
              {shimmerBox(100, 16, 6)}
            </div>
            {shimmerBox("100%", 220, 8)}
          </div>
        ))}
      </div>
    </div>
  );

  // ── KPI Cards ──
  const renderKPIs = () => {
    const cards = [
      /* Bracketed, not prefixed with a plus. These marks are part of the total
         above, so "+57" read as a figure to be added on top of it. */
      { label: "Total Trademarks", value: fmt(db.total_trademarks), icon: <FileProtectOutlined />, color: "#6366f1", bg: "#eef2ff", sub: db.user_added_trademarks_count ? `(incl. ${fmt(db.user_added_trademarks_count)} user added)` : null, tab: "filings" },
      { label: "Inbound Oppositions", value: fmt(db.inbound_opposition_count), icon: <AlertOutlined />, color: "#f97316", bg: "#fff7ed", sub: null, tab: "inbound" },
      { label: "TM Renewals Due", value: fmt(db.renewal_due_count), icon: <ClockCircleOutlined />, color: "#ef4444", bg: "#fef2f2", sub: db.renewal_due_breakdown?.due_now ? `${db.renewal_due_breakdown.due_now} expiring soon` : null, tab: "renewals" },
      { label: "Today's Hearings", value: fmt(hearings.length), icon: <CalendarOutlined />, color: "#10b981", bg: "#f0fdf4", sub: null, tab: "hearings" },
    ];
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 16, marginBottom: 24 }}>
        {cards.map((c) => (
          <div key={c.label} onClick={() => setActiveTab(c.tab)} style={{ ...cardStyle, padding: "22px 20px", display: "flex", alignItems: "center", gap: 16, cursor: "pointer", transition: "box-shadow 0.15s, transform 0.15s" }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = cardStyle.boxShadow; e.currentTarget.style.transform = "none"; }}
          >
            <div style={{ width: 52, height: 52, borderRadius: 14, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: c.color, flexShrink: 0 }}>
              {c.icon}
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500, marginBottom: 2 }}>{c.label}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1.1, letterSpacing: -1 }}>{c.value}</div>
              {c.sub && <div style={{ fontSize: 11, color: c.color, fontWeight: 600, marginTop: 2 }}>{c.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── Needs Attention Banner ──
  const renderUrgentBanner = () => {
    if (urgentDeadlines.length === 0) return null;
    return (
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={{ padding: "16px 24px 0", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>🔥</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>Urgent Deadlines</span>
          <span style={{ marginLeft: "auto", padding: "2px 10px", borderRadius: 12, background: "#fef2f2", color: "#dc2626", fontSize: 12, fontWeight: 700 }}>{urgentDeadlines.length}</span>
        </div>
        <div style={{ padding: "12px 24px 16px" }}>
          {urgentDeadlines.map((d, i) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < urgentDeadlines.length - 1 ? "1px solid #f1f5f9" : "none" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: d.urgency === "OVERDUE" ? "#ef4444" : "#f59e0b", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>{d.trade_mark_data?.name || d.tm_number}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{d.notice_label}</div>
              </div>
              <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, color: d.urgency === "OVERDUE" ? "#dc2626" : "#d97706", background: d.urgency === "OVERDUE" ? "#fef2f2" : "#fffbeb", whiteSpace: "nowrap" }}>
                {d.countdown_days < 0 ? `${Math.abs(d.countdown_days)}d overdue` : `${d.countdown_days}d left`}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Status Distribution ──
  const renderStatusChart = () => {
    if (topStatuses.length === 0) return null;
    return (
      <div style={{ ...cardStyle, flex: 1, minWidth: 320 }}>
        {sectionTitle(<AuditOutlined />, "Status Distribution", null, "linear-gradient(135deg,#8b5cf6,#7c3aed)")}
        <div style={{ padding: "16px 24px", height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={topStatuses} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} strokeWidth={0}>
                {topStatuses.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <RTooltip formatter={(val, name) => [fmt(val), name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ padding: "0 24px 16px", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {topStatuses.map((s, i) => (
            <span key={s.status} style={{ fontSize: 11, color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: PIE_COLORS[i % PIE_COLORS.length], display: "inline-block" }} />
              {s.status} ({fmt(s.count)})
            </span>
          ))}
        </div>
      </div>
    );
  };

  // ── Filing Trends ──
  const renderFilingTrends = () => {
    if (yearlyFilings.length === 0) return null;
    return (
      <div style={{ ...cardStyle, flex: 1, minWidth: 320 }}>
        {sectionTitle(<ThunderboltOutlined />, "Yearly Filing Trends", null, "linear-gradient(135deg,#3b82f6,#2563eb)")}
        <div style={{ padding: "16px 24px", height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={yearlyFilings}>
              <defs>
                <linearGradient id="filingGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <RTooltip formatter={(v) => [fmt(v), "Trademarks"]} />
              <Area type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} fill="url(#filingGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // ── Geographic Distribution ──
  const renderGeoChart = () => {
    if (topStates.length === 0) return null;
    return (
      <div style={{ ...cardStyle, flex: 1, minWidth: 320 }}>
        {sectionTitle(<GlobalOutlined />, "Top States", null, "linear-gradient(135deg,#10b981,#059669)")}
        <div style={{ padding: "16px 24px", height: Math.max(280, topStates.length * 24 + 40), maxHeight: 500, overflowY: "auto" }}>
          <ResponsiveContainer width="100%" height={Math.max(280, topStates.length * 24)}>
            <BarChart data={topStates} layout="vertical" margin={{ left: 100 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis dataKey="state" type="category" tick={{ fontSize: 9, fill: "#64748b" }} width={100} />
              <RTooltip formatter={(v) => [fmt(v), "Trademarks"]} />
              <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} barSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // ── Class Distribution ──
  const renderClassChart = () => {
    if (topClasses.length === 0) return null;
    return (
      <div style={{ ...cardStyle, flex: 1, minWidth: 320 }}>
        {sectionTitle(<BookOutlined />, "Class Distribution", `${topClasses.length} of 45 classes`, "linear-gradient(135deg,#f59e0b,#d97706)")}
        <div style={{ padding: "16px 24px 8px" }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={topClasses} margin={{ bottom: 30, top: 10 }}>
              <defs>
                <linearGradient id="classGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="class_number" tick={{ fontSize: 10, fill: "#64748b", fontWeight: 600 }} interval={0} angle={-45} textAnchor="end" height={45} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
              <RTooltip formatter={(v, n, p) => [fmt(v), `Class ${p.payload.class_number}`]} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
              <Bar dataKey="count" fill="url(#classGrad)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // ── Overview Tab ──
  const renderOverview = () => (
    <div>
      <AccountSummaryCards
        summary={summary}
        loading={summaryLoading}
        onOpenTab={(panel) => { setAccountPanel(panel); setActiveTab("account"); }}
      />
      {renderKPIs()}
      <div style={{ display: "grid", gridTemplateColumns: urgentDeadlines.length > 0 ? "1fr 380px" : "1fr", gap: 20, marginBottom: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {renderStatusChart()}
            {renderFilingTrends()}
          </div>
        </div>
        {urgentDeadlines.length > 0 && renderUrgentBanner()}
      </div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        {renderGeoChart()}
        {renderClassChart()}
      </div>
    </div>
  );

  // ── Trademarks Tab ──
  const [tmFilterName, setTmFilterName] = useState("");
  const [tmFilterNumber, setTmFilterNumber] = useState("");
  const [tmFilterClass, setTmFilterClass] = useState("");
  const [tmFilterStatus, setTmFilterStatus] = useState("");
  const [tmFilterAttorney, setTmFilterAttorney] = useState("");
  const [tmFilterDateFrom, setTmFilterDateFrom] = useState("");
  const [tmFilterDateTo, setTmFilterDateTo] = useState("");

  const tmAgg = filings?.aggregations || {};
  const tmAttorneyList = useMemo(() => tmAgg.attorney_names || [], [tmAgg.attorney_names]);
  const tmStatusList = useMemo(() => tmAgg.status || [], [tmAgg.status]);
  const tmClassList = useMemo(() => tmAgg.class_distribution || [], [tmAgg.class_distribution]);

  const tmFilters = () => ({
    wordmark: tmFilterName || undefined,
    trade_mark_number: tmFilterNumber || undefined,
    trade_mark_class: tmFilterClass || undefined,
    status: tmFilterStatus || undefined,
    attorney_name: tmFilterAttorney || undefined,
    date_from: tmFilterDateFrom || undefined,
    date_to: tmFilterDateTo || undefined,
  });

  const searchTm = (overrides = {}) => {
    const f = { ...tmFilters(), ...overrides };
    Object.keys(f).forEach((k) => { if (f[k] === "" || f[k] === undefined || f[k] === null) delete f[k]; });
    setFilingsPage(1); setFilingsLoading(true);
    fetchAttorneyFilings(id, 1, 20, f)
      .then((res) => { setFilings(res); })
      .catch(() => setFilings(null))
      .finally(() => setFilingsLoading(false));
  };

  const resetTmFilters = () => {
    setTmFilterName(""); setTmFilterNumber(""); setTmFilterClass(""); setTmFilterStatus("");
    setTmFilterAttorney(""); setTmFilterDateFrom(""); setTmFilterDateTo("");
    setFilingsPage(1); setFilingsLoading(true);
    fetchAttorneyFilings(id, 1, 20)
      .then((res) => { setFilings(res); })
      .catch(() => setFilings(null))
      .finally(() => setFilingsLoading(false));
  };

  const loadFilingsWithFilters = (page) => {
    setFilingsLoading(true);
    fetchAttorneyFilings(id, page, 20, tmFilters())
      .then((res) => { setFilings(res); setFilingsPage(page); })
      .catch(() => setFilings(null))
      .finally(() => setFilingsLoading(false));
  };

  const filingsColumns = [
    {
      title: "Trademark",
      key: "tm",
      width: 280,
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {tmLogo(r.logo_url, r.name)}
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{r.name}</div>
            <div style={{ fontSize: 11, color: "#6366f1", fontFamily: "monospace" }}>TM-{r.trade_mark}</div>
          </div>
        </div>
      ),
    },
    { title: "Class", dataIndex: "class", key: "class", width: 70, render: (v) => <Tag color="blue">{v}</Tag> },
    { title: "Status", dataIndex: "status", key: "status", width: 180, render: (v) => statusTag(v) },
    { title: "Type", dataIndex: "trade_mark_type", key: "type", width: 80, render: (v) => <span style={{ fontSize: 12, color: "#64748b" }}>{v}</span> },
    { title: "Filed", dataIndex: "date_of_application", key: "date", width: 110, render: (v) => <span style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>{v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</span> },
    { title: "Attorney", dataIndex: "attorney_name", key: "attorney", width: 160, render: (v) => <span style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>{(v || "").replace(/\[\d+\]/, "").trim() || "—"}</span> },
    { title: "Owner", dataIndex: "proprietor_name", key: "owner", width: 160, render: (v) => { const clean = (v || "").replace(/^\(\d+\)\s*/, "").replace(/(Single Firm|Body Incorporate|Partnership Firm Details.*)$/i, "").trim(); return <Tooltip title={v}><span style={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>{clean || "—"}</span></Tooltip>; } },
    { title: "State", dataIndex: "state", key: "state", width: 130, render: (v) => <span style={{ fontSize: 12, color: "#64748b", textTransform: "capitalize" }}>{(v || "").toLowerCase()}</span> },
  ];

  const inputS = { height: 36, borderRadius: 8, border: "1px solid #e2e8f0", padding: "0 10px", fontSize: 13, color: "#374151", outline: "none", background: "#fff", width: "100%" };
  const labelS = { fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 };

  const renderFilings = () => (
    <div>
      {/* Filters card */}
      <div style={{ ...cardStyle, padding: "18px 24px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SearchOutlined style={{ color: "#6366f1" }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Filters & Search</span>
          </div>
          <span style={{ fontSize: 13, color: "#6366f1", fontWeight: 600 }}>{fmt(filings?.total || 0)} results</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={labelS}>TM Name</div>
            <input value={tmFilterName} onChange={(e) => setTmFilterName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchTm()} placeholder="Search trademark..." style={inputS} />
          </div>
          <div>
            <div style={labelS}>TM Number</div>
            <input value={tmFilterNumber} onChange={(e) => setTmFilterNumber(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchTm()} placeholder="Application no..." style={inputS} />
          </div>
          <div>
            <div style={labelS}>Class</div>
            <Select value={tmFilterClass || undefined} placeholder="All classes" allowClear onChange={(v) => { setTmFilterClass(v || ""); searchTm({ trade_mark_class: v || undefined }); }} style={{ width: "100%", height: 36 }} showSearch>
              {(tmClassList.length > 0 ? tmClassList : topClasses).map((c) => <Option key={c.class_number} value={c.class_number}>{c.class_number} ({fmt(c.count)})</Option>)}
            </Select>
          </div>
          <div>
            <div style={labelS}>Status</div>
            <Select value={tmFilterStatus || undefined} placeholder="All statuses" allowClear onChange={(v) => { setTmFilterStatus(v || ""); searchTm({ status: v || undefined }); }} style={{ width: "100%", height: 36 }} showSearch>
              {(tmStatusList.length > 0 ? tmStatusList : topStatuses).map((s) => <Option key={s.status} value={s.status} label={s.status}><span style={{ color: STATUS_COLORS[s.status] || STATUS_COLORS.default, fontWeight: 600 }}>{s.status}</span> <span style={{ color: "#94a3b8" }}>({fmt(s.count)})</span></Option>)}
            </Select>
          </div>
          <div>
            <div style={labelS}>Attorney</div>
            <Select value={tmFilterAttorney || undefined} placeholder="All attorneys" allowClear onChange={(v) => { setTmFilterAttorney(v || ""); searchTm({ attorney_name: v || undefined }); }} style={{ width: "100%", height: 36 }} showSearch filterOption={(input, option) => (option?.children || "").toLowerCase().includes(input.toLowerCase())}>
              {tmAttorneyList.map((a) => <Option key={a.attorney_name} value={a.attorney_name}>{a.attorney_name} ({fmt(a.count)})</Option>)}
            </Select>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={labelS}>Application Date Range</div>
          <div style={{ display: "inline-flex", alignItems: "center", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", height: 36 }}>
            <input type="date" value={tmFilterDateFrom} onChange={(e) => setTmFilterDateFrom(e.target.value)} style={{ border: "none", padding: "0 10px", fontSize: 13, color: "#475569", outline: "none", height: "100%", width: 135 }} />
            <span style={{ color: "#cbd5e1", fontSize: 12, padding: "0 4px" }}>→</span>
            <input type="date" value={tmFilterDateTo} onChange={(e) => setTmFilterDateTo(e.target.value)} style={{ border: "none", padding: "0 10px", fontSize: 13, color: "#475569", outline: "none", height: "100%", width: 135 }} />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {(tmFilterName || tmFilterNumber || tmFilterClass || tmFilterStatus || tmFilterAttorney || tmFilterDateFrom || tmFilterDateTo) && (
            <button onClick={resetTmFilters} style={{ padding: "0 16px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <i className="bx bx-reset" style={{ fontSize: 14 }} /> Reset
            </button>
          )}
          <button onClick={searchTm} style={{ padding: "0 20px", height: 36, borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <SearchOutlined /> Search
          </button>
          {exportBtn(handleTmExport)}
        </div>
      </div>

      {/* Table */}
      <div style={cardStyle}>
        <div style={{ padding: "12px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>Total: <span style={{ color: "#6366f1" }}>{fmt(filings?.total || 0)}</span></span>
        </div>
        <div style={{ padding: "0 24px 16px" }}>
          <Table
            dataSource={filings?.data || []}
            columns={filingsColumns}
            rowKey={(r) => r.id || r.trade_mark}
            loading={filingsLoading}
            size="small"
            scroll={{ x: 1000 }}
            pagination={{
              current: filingsPage,
              pageSize: 20,
              total: filings?.total || 0,
              showSizeChanger: false,
              onChange: (p) => loadFilingsWithFilters(p),
            }}
          />
        </div>
      </div>
    </div>
  );

  // ── Inbound Oppositions Tab ──
  const STAGE_COLORS = {
    NOTICE_OF_OPPOSITION: { color: "#2563eb", bg: "#eff6ff" },
    COUNTER_STATEMENT_FILED: { color: "#7c3aed", bg: "#f5f3ff" },
    EVIDENCE_45_FILED: { color: "#f59e0b", bg: "#fffbeb" },
    EVIDENCE_46_FILED: { color: "#f97316", bg: "#fff7ed" },
    EVIDENCE_47_FILED: { color: "#ef4444", bg: "#fef2f2" },
    ABANDONED: { color: "#6b7280", bg: "#f9fafb" },
    HEARING_SCHEDULED: { color: "#8b5cf6", bg: "#f5f3ff" },
    WITHDRAWN: { color: "#9ca3af", bg: "#f8fafc" },
  };

  const stageTag = (stage) => {
    const c = STAGE_COLORS[stage] || { color: "#64748b", bg: "#f8fafc" };
    return <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, color: c.color, background: c.bg, whiteSpace: "nowrap" }}>{(stage || "—").replace(/_/g, " ")}</span>;
  };

  const inboundColumns = [
    {
      title: "Application Details",
      key: "app",
      width: 220,
      render: (_, r) => {
        const td = r.trade_mark_data || {};
        return (
          <div>
            <div style={{ fontSize: 13, color: "#6366f1", fontWeight: 700, fontFamily: "monospace" }}>{r.tm_number}</div>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{td.name}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              <span>📅 {td.date_of_application ? new Date(td.date_of_application).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
            </div>
            <div style={{ fontSize: 11, color: "#64748b" }}>Class : {td.class}</div>
          </div>
        );
      },
    },
    {
      title: "Opposition Details",
      key: "opp",
      width: 220,
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 13, color: "#ef4444", fontWeight: 700, fontFamily: "monospace" }}>{r.opposition_tm_number} <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 11 }}>ⓘ</span></div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            📅 {r.rec_date ? new Date(r.rec_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b", marginTop: 2 }}>{r.opponent_name}</div>
          {r.opponent_attorney_name && <div style={{ fontSize: 11, color: "#64748b" }}>⚖ {r.opponent_attorney_name}</div>}
        </div>
      ),
    },
    { title: "Status", dataIndex: "status", key: "status", width: 120, render: (v) => statusTag(v) },
    { title: "Stage", dataIndex: "current_stage", key: "stage", width: 160, render: (v) => stageTag(v) },
    { title: "Urgency", dataIndex: "urgency_level", key: "urg", width: 100, render: (v) => urgencyTag(v) },
    { title: "Countdown", dataIndex: "countdown_days", key: "days", width: 140, render: (v) => { if (v == null) return "—"; const isOverdue = v < 0; const color = isOverdue ? "#dc2626" : v <= 7 ? "#f97316" : v <= 30 ? "#f59e0b" : "#10b981"; const bg = isOverdue ? "#fef2f2" : v <= 7 ? "#fff7ed" : v <= 30 ? "#fffbeb" : "#f0fdf4"; const border = isOverdue ? "#fecaca" : v <= 7 ? "#fed7aa" : v <= 30 ? "#fde68a" : "#bbf7d0"; const icon = isOverdue ? "⊘" : v <= 7 ? "⚠" : v <= 30 ? "◔" : "✓"; return <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, color, background: bg, border: `1px solid ${border}`, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>{icon} {isOverdue ? `${Math.abs(v)} days overdue` : `${v} days`}</span>; } },
    { title: "Deadline", dataIndex: "next_deadline", key: "dl", width: 110, render: (v) => <span style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>{v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</span> },
    { title: "Docs", dataIndex: "documents_count", key: "docs", width: 60, render: (v) => <Badge count={v || 0} style={{ backgroundColor: "#6366f1" }} /> },
  ];

  // Inbound filters
  const [ibName, setIbName] = useState("");
  const [ibTmNo, setIbTmNo] = useState("");
  const [ibClass, setIbClass] = useState("");
  const [ibStage, setIbStage] = useState("");
  const [ibDeadlineType, setIbDeadlineType] = useState("");
  const [ibUrgency, setIbUrgency] = useState("");
  const [ibDateFrom, setIbDateFrom] = useState("");
  const [ibDateTo, setIbDateTo] = useState("");

  const ibAgg = inbound?.opposition_aggregations || {};
  const ibTmAgg = inbound?.trademark_aggregations || {};

  const ibFilters = () => ({
    wordmark: ibName || undefined,
    trade_mark_number: ibTmNo || undefined,
    class_number: ibClass || undefined,
    current_stage: ibStage || undefined,
    deadline_type: ibDeadlineType || undefined,
    urgency_level: ibUrgency || undefined,
    deadline_from: ibDateFrom || undefined,
    deadline_to: ibDateTo || undefined,
  });

  const searchIb = (overrides = {}) => {
    const f = { ...ibFilters(), ...overrides };
    Object.keys(f).forEach((k) => { if (f[k] === "" || f[k] === undefined || f[k] === null) delete f[k]; });
    setInboundPage(1); setInboundLoading(true);
    fetchInboundOppositions(id, 1, 20, f)
      .then((res) => setInbound(res)).catch(() => setInbound(null)).finally(() => setInboundLoading(false));
  };

  const resetIb = () => {
    setIbName(""); setIbTmNo(""); setIbClass(""); setIbStage(""); setIbDeadlineType(""); setIbUrgency(""); setIbDateFrom(""); setIbDateTo("");
    setInboundPage(1); setInboundLoading(true);
    fetchInboundOppositions(id, 1, 20)
      .then((res) => setInbound(res)).catch(() => setInbound(null)).finally(() => setInboundLoading(false));
  };

  const loadIbPage = (page) => {
    setInboundLoading(true);
    fetchInboundOppositions(id, page, 20, ibFilters())
      .then((res) => { setInbound(res); setInboundPage(page); }).catch(() => setInbound(null)).finally(() => setInboundLoading(false));
  };

  const renderInbound = () => (
    <div>
      {/* Filters */}
      <div style={{ ...cardStyle, padding: "18px 24px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SearchOutlined style={{ color: "#6366f1" }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Filters & Search</span>
          </div>
          <span style={{ fontSize: 13, color: "#6366f1", fontWeight: 600 }}>{fmt(inbound?.total || 0)} results</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={labelS}>TM Name</div>
            <input value={ibName} onChange={(e) => setIbName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchIb()} placeholder="Search TM Name..." style={inputS} />
          </div>
          <div>
            <div style={labelS}>TM Number</div>
            <input value={ibTmNo} onChange={(e) => setIbTmNo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchIb()} placeholder="Search TM No..." style={inputS} />
          </div>
          <div>
            <div style={labelS}>Class</div>
            <Select value={ibClass || undefined} placeholder="All Classes" allowClear onChange={(v) => { setIbClass(v || ""); searchIb({ class_number: v || undefined }); }} style={{ width: "100%", height: 36 }} showSearch>
              {(ibTmAgg.class || []).map((c) => <Option key={c.class_number} value={c.class_number}>{c.class_number} ({fmt(c.count)})</Option>)}
            </Select>
          </div>
          <div>
            <div style={labelS}>Stage</div>
            <Select value={ibStage || undefined} placeholder="All Stages" allowClear onChange={(v) => { setIbStage(v || ""); searchIb({ current_stage: v || undefined }); }} style={{ width: "100%", height: 36 }}>
              {(ibAgg.current_stage || []).map((s) => { const sc = STAGE_COLORS[s.stage] || { color: "#64748b" }; return <Option key={s.stage} value={s.stage}><span style={{ color: sc.color, fontWeight: 600 }}>{(s.stage || "").replace(/_/g, " ")}</span> <span style={{ color: "#94a3b8" }}>({s.count})</span></Option>; })}
            </Select>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={labelS}>Deadline Type</div>
            <Select value={ibDeadlineType || undefined} placeholder="All Types" allowClear onChange={(v) => { setIbDeadlineType(v || ""); searchIb({ deadline_type: v || undefined }); }} style={{ width: "100%", height: 36 }}>
              {(ibAgg.deadline_type || []).map((d) => <Option key={d.deadline} value={d.deadline}>{(d.deadline || "").replace(/_/g, " ")} ({d.count})</Option>)}
            </Select>
          </div>
          <div>
            <div style={labelS}>Urgency</div>
            <Select value={ibUrgency || undefined} placeholder="All Levels" allowClear onChange={(v) => { setIbUrgency(v || ""); searchIb({ urgency_level: v || undefined }); }} style={{ width: "100%", height: 36 }}>
              {(ibAgg.urgency_level || []).map((u) => { const uc = URGENCY_CONFIG[u.urgency] || URGENCY_CONFIG.NONE; return <Option key={u.urgency} value={u.urgency}><span style={{ color: uc.color, fontWeight: 600 }}>{uc.label}</span> <span style={{ color: "#94a3b8" }}>({u.count})</span></Option>; })}
            </Select>
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <div style={labelS}>Deadline Range</div>
            <div style={{ display: "inline-flex", alignItems: "center", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", height: 36 }}>
              <input type="date" value={ibDateFrom} onChange={(e) => setIbDateFrom(e.target.value)} style={{ border: "none", padding: "0 10px", fontSize: 13, color: "#475569", outline: "none", height: "100%", width: 135 }} />
              <span style={{ color: "#cbd5e1", fontSize: 12, padding: "0 4px" }}>→</span>
              <input type="date" value={ibDateTo} onChange={(e) => setIbDateTo(e.target.value)} style={{ border: "none", padding: "0 10px", fontSize: 13, color: "#475569", outline: "none", height: "100%", width: 135 }} />
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {(ibName || ibTmNo || ibClass || ibStage || ibDeadlineType || ibUrgency || ibDateFrom || ibDateTo) && (
            <button onClick={resetIb} style={{ padding: "0 16px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <i className="bx bx-reset" style={{ fontSize: 14 }} /> Reset
            </button>
          )}
          <button onClick={searchIb} style={{ padding: "0 20px", height: 36, borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <SearchOutlined /> Search
          </button>
          {exportBtn(handleIbExport)}
        </div>
      </div>

      {/* Table */}
      <div style={cardStyle}>
        <div style={{ padding: "0 24px 16px" }}>
          <Table
            dataSource={inbound?.data || []}
            columns={inboundColumns}
            rowKey={(r) => r.id}
            loading={inboundLoading}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{
              current: inboundPage,
              pageSize: 20,
              total: inbound?.total || 0,
              showSizeChanger: false,
              onChange: (p) => loadIbPage(p),
            }}
          />
        </div>
      </div>
    </div>
  );

  // ── Outbound Oppositions Tab ──
  const outboundColumns = [
    {
      title: "Target Trademark",
      key: "tm",
      width: 220,
      render: (_, r) => {
        const td = r.tm_details || {};
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {tmLogo(td.logo_url, td.name)}
            <div>
              <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{td.name}</div>
              <div style={{ fontSize: 11, color: "#6366f1", fontFamily: "monospace" }}>TM-{td.trade_mark}</div>
            </div>
          </div>
        );
      },
    },
    { title: "Opponent", dataIndex: "opponent_name", key: "opp", width: 180, render: (v) => <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>{v}</span> },
    { title: "Attorney", dataIndex: "opponent_attorney_name", key: "atty", width: 160, render: (v) => <span style={{ fontSize: 11, color: "#64748b" }}>{v || "—"}</span> },
    { title: "Stage", dataIndex: "current_stage", key: "stage", width: 160, render: (v) => <span style={{ fontSize: 11, color: "#64748b" }}>{(v || "").replace(/_/g, " ")}</span> },
    { title: "Urgency", dataIndex: "urgency_level", key: "urg", width: 100, render: (v) => urgencyTag(v) },
    { title: "Countdown", dataIndex: "countdown_days", key: "days", width: 140, render: (v) => { if (v == null) return "—"; const isOverdue = v < 0; const color = isOverdue ? "#dc2626" : v <= 7 ? "#f97316" : v <= 30 ? "#f59e0b" : "#10b981"; const bg = isOverdue ? "#fef2f2" : v <= 7 ? "#fff7ed" : v <= 30 ? "#fffbeb" : "#f0fdf4"; const border = isOverdue ? "#fecaca" : v <= 7 ? "#fed7aa" : v <= 30 ? "#fde68a" : "#bbf7d0"; const icon = isOverdue ? "⊘" : v <= 7 ? "⚠" : v <= 30 ? "◔" : "✓"; return <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, color, background: bg, border: `1px solid ${border}`, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>{icon} {isOverdue ? `${Math.abs(v)} days overdue` : `${v} days`}</span>; } },
    { title: "Docs", dataIndex: "documents_count", key: "docs", width: 60, render: (v) => <Badge count={v || 0} style={{ backgroundColor: "#f97316" }} /> },
  ];

  // Outbound filters
  const [obName, setObName] = useState("");
  const [obTmNo, setObTmNo] = useState("");
  const [obStage, setObStage] = useState("");
  const [obDeadlineType, setObDeadlineType] = useState("");
  const [obUrgency, setObUrgency] = useState("");
  const [obDateFrom, setObDateFrom] = useState("");
  const [obDateTo, setObDateTo] = useState("");
  const [obAttorney, setObAttorney] = useState("");

  const obAgg = outbound?.opposition_aggregations || outbound?.data?.opposition_aggregations || {};
  const obStages = obAgg.current_stage || [];
  const obDeadlineTypes = obAgg.deadline_type || [];
  const obUrgencies = obAgg.urgency_level || [];
  const obAttorneyAgg = obAgg.opponent_attorney || [];

  const obFilters = (overrides = {}) => {
    const f = { wordmark: obName || undefined, trade_mark_number: obTmNo || undefined, current_stage: obStage || undefined, deadline_type: obDeadlineType || undefined, urgency_level: obUrgency || undefined, deadline_from: obDateFrom || undefined, deadline_to: obDateTo || undefined, opponent_attorney: obAttorney || undefined, ...overrides };
    Object.keys(f).forEach((k) => { if (f[k] === "" || f[k] === undefined || f[k] === null) delete f[k]; });
    return f;
  };

  const searchOb = (overrides = {}) => {
    setOutboundPage(1); setOutboundLoading(true);
    fetchOutboundOppositions(id, 1, 20, obFilters(overrides))
      .then((res) => setOutbound(res)).catch(() => setOutbound(null)).finally(() => setOutboundLoading(false));
  };

  const resetOb = () => {
    setObName(""); setObTmNo(""); setObStage(""); setObDeadlineType(""); setObUrgency(""); setObDateFrom(""); setObDateTo(""); setObAttorney("");
    setOutboundPage(1); setOutboundLoading(true);
    fetchOutboundOppositions(id, 1, 20)
      .then((res) => setOutbound(res)).catch(() => setOutbound(null)).finally(() => setOutboundLoading(false));
  };

  const loadObPage = (page) => {
    setOutboundLoading(true);
    fetchOutboundOppositions(id, page, 20, obFilters())
      .then((res) => { setOutbound(res); setOutboundPage(page); }).catch(() => setOutbound(null)).finally(() => setOutboundLoading(false));
  };

  const renderOutbound = () => (
    <div>
      {/* Filters */}
      <div style={{ ...cardStyle, padding: "18px 24px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SearchOutlined style={{ color: "#6366f1" }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Filters & Search</span>
          </div>
          <span style={{ fontSize: 13, color: "#6366f1", fontWeight: 600 }}>{fmt(outbound?.data?.total || 0)} results</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={labelS}>TM Name</div>
            <input value={obName} onChange={(e) => setObName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchOb()} placeholder="Search..." style={inputS} />
          </div>
          <div>
            <div style={labelS}>TM Number</div>
            <input value={obTmNo} onChange={(e) => setObTmNo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchOb()} placeholder="App no..." style={inputS} />
          </div>
          <div>
            <div style={labelS}>Stage</div>
            <Select value={obStage || undefined} placeholder="All Stages" allowClear onChange={(v) => { setObStage(v || ""); searchOb({ current_stage: v || undefined }); }} style={{ width: "100%", height: 36 }}>
              {obStages.map((s) => { const sc = STAGE_COLORS[s.stage] || { color: "#64748b" }; return <Option key={s.stage} value={s.stage}><span style={{ color: sc.color, fontWeight: 600 }}>{(s.stage || "").replace(/_/g, " ")}</span> <span style={{ color: "#94a3b8" }}>({s.count})</span></Option>; })}
            </Select>
          </div>
          <div>
            <div style={labelS}>Deadline Type</div>
            <Select value={obDeadlineType || undefined} placeholder="All" allowClear onChange={(v) => { setObDeadlineType(v || ""); searchOb({ deadline_type: v || undefined }); }} style={{ width: "100%", height: 36 }}>
              {obDeadlineTypes.map((d) => <Option key={d.deadline} value={d.deadline}>{(d.deadline || "").replace(/_/g, " ")} ({d.count})</Option>)}
            </Select>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={labelS}>Urgency</div>
            <Select value={obUrgency || undefined} placeholder="All Levels" allowClear onChange={(v) => { setObUrgency(v || ""); searchOb({ urgency_level: v || undefined }); }} style={{ width: "100%", height: 36 }}>
              {obUrgencies.map((u) => { const uc = URGENCY_CONFIG[u.urgency] || URGENCY_CONFIG.NONE; return <Option key={u.urgency} value={u.urgency}><span style={{ color: uc.color, fontWeight: 600 }}>{uc.label}</span> <span style={{ color: "#94a3b8" }}>({u.count})</span></Option>; })}
            </Select>
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <div style={labelS}>Deadline Range</div>
            <div style={{ display: "inline-flex", alignItems: "center", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", height: 36 }}>
              <input type="date" value={obDateFrom} onChange={(e) => setObDateFrom(e.target.value)} style={{ border: "none", padding: "0 10px", fontSize: 13, color: "#475569", outline: "none", height: "100%", width: 135 }} />
              <span style={{ color: "#cbd5e1", fontSize: 12, padding: "0 4px" }}>→</span>
              <input type="date" value={obDateTo} onChange={(e) => setObDateTo(e.target.value)} style={{ border: "none", padding: "0 10px", fontSize: 13, color: "#475569", outline: "none", height: "100%", width: 135 }} />
            </div>
          </div>
          <div>
            <div style={labelS}>Opponent Attorney</div>
            <Select value={obAttorney || undefined} placeholder="All Attorneys" allowClear onChange={(v) => { setObAttorney(v || ""); searchOb({ opponent_attorney: v || undefined }); }} style={{ width: "100%", height: 36 }} showSearch popupMatchSelectWidth={false} dropdownStyle={{ minWidth: 300 }} filterOption={(input, option) => (option?.value || "").toLowerCase().includes(input.toLowerCase())}>
              {obAttorneyAgg.map((a) => <Option key={a.attorney} value={a.attorney}>{a.attorney} ({a.count})</Option>)}
            </Select>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {(obName || obTmNo || obStage || obDeadlineType || obUrgency || obDateFrom || obDateTo || obAttorney) && (
            <button onClick={resetOb} style={{ padding: "0 16px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <i className="bx bx-reset" style={{ fontSize: 14 }} /> Reset
            </button>
          )}
          <button onClick={searchOb} style={{ padding: "0 20px", height: 36, borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <SearchOutlined /> Search
          </button>
          {exportBtn(handleObExport)}
        </div>
      </div>

      {/* Table */}
      <div style={cardStyle}>
        <div style={{ padding: "0 24px 16px" }}>
          <Table
            dataSource={outbound?.data?.oppositions || []}
            columns={outboundColumns}
            rowKey={(r) => r.id}
            loading={outboundLoading}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{
              current: outboundPage,
              pageSize: 20,
              total: outbound?.data?.total || 0,
              showSizeChanger: false,
              onChange: (p) => loadObPage(p),
            }}
          />
        </div>
      </div>
    </div>
  );

  // ── Hearings & Deadlines Tab ──
  const deadlineColumns = [
    {
      title: "Trademark",
      key: "tm",
      width: 240,
      render: (_, r) => {
        const td = r.trade_mark_data || {};
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {tmLogo(td.logo_url, td.name)}
            <div>
              <div style={{ fontSize: 12, color: "#6366f1", fontFamily: "monospace", fontWeight: 600 }}>{r.tm_number}</div>
              <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{td.name}</div>
              <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: "#eff6ff", color: "#2563eb" }}>Class {td.class}</span>
                {statusTag(td.status)}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Attorney",
      key: "attorney",
      width: 160,
      render: (_, r) => {
        const name = (r.trade_mark_data?.attorney_name || "").replace(/\[\d+\]/, "").trim();
        return <span style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>{name || "—"}</span>;
      },
    },
    {
      title: "Owner",
      key: "owner",
      width: 160,
      render: (_, r) => {
        const raw = r.trade_mark_data?.proprietor_name || "";
        const clean = raw.replace(/^\(\d+\)\s*/, "").replace(/(Single Firm|Body Incorporate|Partnership Firm Details.*)$/i, "").trim();
        return (
          <Tooltip title={raw}>
            <span style={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>{clean || "—"}</span>
          </Tooltip>
        );
      },
    },
    { title: "Type", dataIndex: "notice_label", key: "type", width: 140, render: (v) => <span style={{ fontSize: 12, color: "#475569" }}>{v}</span> },
    { title: "Category", dataIndex: "category", key: "cat", width: 100, render: (v) => <Tag color={v === "opposition" ? "orange" : v === "hearing" ? "red" : "blue"} style={{ textTransform: "capitalize" }}>{v}</Tag> },
    { title: "Urgency", dataIndex: "urgency", key: "urg", width: 100, render: (v) => urgencyTag(v) },
    { title: "Countdown", dataIndex: "countdown_days", key: "days", width: 140, render: (v) => { if (v == null) return "—"; const isOverdue = v < 0; const color = isOverdue ? "#dc2626" : v <= 7 ? "#f97316" : v <= 30 ? "#f59e0b" : "#10b981"; const bg = isOverdue ? "#fef2f2" : v <= 7 ? "#fff7ed" : v <= 30 ? "#fffbeb" : "#f0fdf4"; const border = isOverdue ? "#fecaca" : v <= 7 ? "#fed7aa" : v <= 30 ? "#fde68a" : "#bbf7d0"; const icon = isOverdue ? "⊘" : v <= 7 ? "⚠" : v <= 30 ? "◔" : "✓"; return <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, color, background: bg, border: `1px solid ${border}`, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>{icon} {isOverdue ? `${Math.abs(v)}d overdue` : `${v}d left`}</span>; } },
    { title: "Due Date", dataIndex: "due_date", key: "due", width: 110, render: (v) => <span style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>{v || "—"}</span> },
  ];

  const renderHearings = () => (
    <div style={cardStyle}>
      {sectionTitle(<CalendarOutlined />, "Today's Hearings", `${hearings.length} hearing${hearings.length !== 1 ? "s" : ""} scheduled`, "linear-gradient(135deg,#10b981,#059669)")}
      <div style={{ padding: "16px 24px" }}>
        {hearings.length > 0 ? hearings.map((h) => (
          <div key={h.tm_number} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
            {tmLogo(h.trademark_details?.logo_url, h.trademark_details?.name)}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: "#1e293b" }}>{h.trademark_details?.name}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>TM-{h.tm_number} | Class {h.trademark_details?.class} | {h.hearing_type?.replace(/_/g, " ")}</div>
            </div>
            {statusTag(h.trademark_details?.status)}
          </div>
        )) : <Empty description="No hearings scheduled for today" />}
      </div>
    </div>
  );

  const [deadlineCatFilter, setDeadlineCatFilter] = useState("all");
  const [dlSearch, setDlSearch] = useState("");
  const dlSearchTimer = useRef(null);
  const [dlUrgency, setDlUrgency] = useState("");
  const [dlDateRange, setDlDateRange] = useState(["", ""]);
  const [dlAttorney, setDlAttorney] = useState("");
  const [dlLoading, setDlLoading] = useState(false);

  const [dlAttorneyList, setDlAttorneyList] = useState([]);

  // Load attorney list from outbound oppositions API on mount
  useEffect(() => {
    if (id) {
      fetchOutboundOppositions(id, 1, 1).then((res) => {
        setDlAttorneyList(res?.data?.attorney_names_found || []);
      }).catch(() => {});
    }
  }, [id]);

  const buildDlFilters = (overrides = {}) => {
    const f = {
      category: deadlineCatFilter !== "all" ? deadlineCatFilter : undefined,
      urgency: dlUrgency || undefined,
      search: dlSearch || undefined,
      attorney: dlAttorney || undefined,
      due_date_start: dlDateRange[0] || undefined,
      due_date_end: dlDateRange[1] || undefined,
      ...overrides,
    };
    Object.keys(f).forEach((k) => { if (f[k] === "" || f[k] === undefined || f[k] === null) delete f[k]; });
    return f;
  };

  const reloadDeadlines = async (overrides = {}) => {
    setDlLoading(true);
    try {
      const res = await fetchAllDeadlines(id, buildDlFilters(overrides));
      setDeadlines(res || {});
    } catch { /* keep existing */ }
    finally { setDlLoading(false); }
  };

  const handleDlCatChange = (cat) => {
    setDeadlineCatFilter(cat);
    reloadDeadlines({ category: cat !== "all" ? cat : undefined });
  };

  const handleDlFilterApply = () => reloadDeadlines();

  const exportCSV = (rows, filename) => {
    if (!rows.length) return;
    const header = Object.keys(rows[0]).join(",");
    const csv = [header, ...rows.map((r) => Object.values(r).map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click(); URL.revokeObjectURL(a.href);
  };

  const exportBtn = (onClick) => (
    <button onClick={onClick} style={{ padding: "0 16px", height: 36, borderRadius: 8, border: "none", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, marginLeft: "auto", boxShadow: "0 2px 8px rgba(16,185,129,0.3)" }}>
      <DownloadOutlined /> Export
    </button>
  );

  const handleTmExport = () => exportCSV((filings?.data || []).map((r) => ({
    TM_Number: r.trade_mark, Name: r.name, Class: r.class, Status: r.status, Type: r.trade_mark_type,
    Filed: r.date_of_application, Attorney: r.attorney_name, Owner: r.proprietor_name, State: r.state,
  })), `Trademarks_Page${filingsPage}_User_${id}.csv`);

  const handleIbExport = () => exportCSV((inbound?.data || []).map((r) => ({
    TM_Number: r.tm_number, Name: r.trade_mark_data?.name, Class: r.trade_mark_data?.class, Status: r.status,
    Opposition_No: r.opposition_tm_number, Opponent: r.opponent_name, Opponent_Attorney: r.opponent_attorney_name,
    Stage: r.current_stage, Urgency: r.urgency_level, Days: r.countdown_days, Deadline: r.next_deadline,
  })), `Inbound_Page${inboundPage}_User_${id}.csv`);

  const handleObExport = () => exportCSV((outbound?.data?.oppositions || []).map((r) => ({
    Target_TM: r.tm_details?.trade_mark, Target_Name: r.tm_details?.name, Opponent: r.opponent_name,
    Attorney: r.opponent_attorney_name, Stage: r.current_stage, Urgency: r.urgency_level,
    Days: r.countdown_days, Docs: r.documents_count,
  })), `Outbound_Page${outboundPage}_User_${id}.csv`);

  const handleDlExport = () => exportCSV((deadlines?.data || []).map((d) => ({
    TM_Number: d.tm_number, Trademark: d.trade_mark_data?.name, Class: d.trade_mark_data?.class,
    Status: d.trade_mark_data?.status, Attorney: d.trade_mark_data?.attorney_name,
    Owner: d.trade_mark_data?.proprietor_name, Notice: d.notice_label, Category: d.category,
    Urgency: d.urgency, Due_Date: d.due_date, Countdown: d.countdown_days,
  })), `Deadlines_User_${id}.csv`);

  const dlStats = deadlines?.stats || {};
  const dlByCat = dlStats.by_category || {};
  const dlPagination = deadlines?.pagination || {};
  const deadlinesList = deadlines?.data || [];

  const deadlineStats = {
    overdue: dlStats.overdue ?? 0,
    d7: dlStats.due_7 ?? 0,
    d15: dlStats.due_15 ?? 0,
    d30: dlStats.due_30 ?? 0,
    total: dlPagination.total ?? dlStats.total ?? deadlinesList.length,
    prosecution: dlByCat.prosecution ?? 0,
    opposition: dlByCat.opposition ?? 0,
    hearing: dlByCat.hearing ?? 0,
    renewal: dlByCat.renewal ?? 0,
  };

  const filteredDeadlines = useMemo(() => {
    if (deadlineCatFilter === "all") return deadlinesList;
    return deadlinesList.filter((d) => d.category === deadlineCatFilter);
  }, [deadlinesList, deadlineCatFilter]);

  const renderDeadlines = () => {
    const statCards = [
      { label: "OVERDUE", value: deadlineStats.overdue, color: "#ef4444", icon: "🚨" },
      { label: "\u2264 7 DAYS", value: deadlineStats.d7, color: "#f97316", icon: "⚡" },
      { label: "\u2264 15 DAYS", value: deadlineStats.d15, color: "#f59e0b", icon: "⏳" },
      { label: "\u2264 30 DAYS", value: deadlineStats.d30, color: "#22c55e", icon: "📅" },
      { label: "TOTAL", value: deadlineStats.total, color: "#64748b", icon: "📋" },
    ];
    const catPills = [
      { key: "all", label: "All", count: deadlineStats.total, color: "#6366f1", bg: "#eef2ff", activeBg: "#6366f1" },
      { key: "prosecution", label: "Prosecution", count: deadlineStats.prosecution, color: "#2563eb", bg: "#eff6ff", activeBg: "#2563eb" },
      { key: "opposition", label: "Opposition", count: deadlineStats.opposition, color: "#f97316", bg: "#fff7ed", activeBg: "#f97316" },
      { key: "hearing", label: "Hearing", count: deadlineStats.hearing, color: "#8b5cf6", bg: "#f5f3ff", activeBg: "#8b5cf6" },
      { key: "renewal", label: "Renewal", count: deadlineStats.renewal, color: "#10b981", bg: "#f0fdf4", activeBg: "#10b981" },
    ];

    return (
      <div>
        {/* Premium stat cards */}
        <div style={{ ...cardStyle, padding: "24px 28px", marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 16 }}>
            {statCards.map((s) => (
              <div key={s.label} style={{
                padding: "20px 16px", borderRadius: 14, textAlign: "center",
                background: `linear-gradient(145deg, ${s.color}08, ${s.color}04)`,
                border: `1px solid ${s.color}15`,
                boxShadow: `0 2px 8px ${s.color}08`,
                transition: "transform 0.2s, box-shadow 0.2s",
                cursor: "default",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${s.color}15`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 2px 8px ${s.color}08`; }}
              >
                <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
                <div style={{ fontSize: 34, fontWeight: 800, color: s.color, lineHeight: 1, letterSpacing: -1 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginTop: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Category filter pills */}
        <div style={{ ...cardStyle, padding: "14px 24px", marginBottom: 20, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {catPills.map((c) => {
            const active = deadlineCatFilter === c.key;
            return (
              <button key={c.key} onClick={() => handleDlCatChange(c.key)} style={{
                padding: "8px 18px", borderRadius: 24,
                border: active ? `2px solid ${c.color}` : "1.5px solid #e2e8f0",
                background: active ? c.bg : "#fff",
                color: active ? c.color : "#94a3b8",
                fontWeight: 600, fontSize: 13, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: active ? `0 2px 8px ${c.color}20` : "none",
                transition: "all 0.2s",
              }}>
                {c.label}
                <span style={{
                  padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 800,
                  background: active ? c.color : "#f1f5f9",
                  color: active ? "#fff" : "#94a3b8",
                }}>{c.count}</span>
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        <div style={{ ...cardStyle, padding: "14px 20px", marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            placeholder="Search TM or wordmark..."
            value={dlSearch}
            onChange={(e) => setDlSearch(e.target.value)}
            onPressEnter={() => reloadDeadlines({ search: dlSearch || undefined })}
            allowClear
            onClear={() => { setDlSearch(""); reloadDeadlines({ search: undefined }); }}
            style={{ width: 200, borderRadius: 8, height: 36 }}
          />
          <Select
            value={dlUrgency || undefined}
            placeholder="All Urgency"
            allowClear
            onChange={(v) => { setDlUrgency(v || ""); reloadDeadlines({ urgency: v || undefined }); }}
            style={{ width: 150, height: 36 }}
          >
            <Option value="OVERDUE"><span style={{ color: "#dc2626", fontWeight: 600 }}>Overdue</span></Option>
            <Option value="CRITICAL"><span style={{ color: "#f97316", fontWeight: 600 }}>{"Critical (\u22647d)"}</span></Option>
            <Option value="URGENT"><span style={{ color: "#f59e0b", fontWeight: 600 }}>{"Urgent (\u226415d)"}</span></Option>
            <Option value="NORMAL"><span style={{ color: "#22c55e", fontWeight: 600 }}>Normal</span></Option>
          </Select>
          <div style={{ display: "flex", alignItems: "center", gap: 0, border: "1px solid #d9d9d9", borderRadius: 8, overflow: "hidden", height: 36 }}>
            <input type="date" value={dlDateRange[0]} onChange={(e) => setDlDateRange([e.target.value, dlDateRange[1]])} style={{ border: "none", padding: "0 10px", fontSize: 13, color: "#475569", outline: "none", height: "100%", width: 130 }} placeholder="Start date" />
            <span style={{ color: "#cbd5e1", fontSize: 12 }}>→</span>
            <input type="date" value={dlDateRange[1]} onChange={(e) => setDlDateRange([dlDateRange[0], e.target.value])} style={{ border: "none", padding: "0 10px", fontSize: 13, color: "#475569", outline: "none", height: "100%", width: 130 }} placeholder="End date" />
          </div>
          <Select
            value={dlAttorney || undefined}
            placeholder="All Attorneys"
            allowClear
            onChange={(v) => { setDlAttorney(v || ""); reloadDeadlines({ attorney: v || undefined }); }}
            style={{ width: 170, height: 36 }}
            showSearch
            filterOption={(input, option) => (option?.children || "").toLowerCase().includes(input.toLowerCase())}
          >
            {dlAttorneyList.map((a) => <Option key={a} value={a}>{a}</Option>)}
          </Select>
          <button onClick={handleDlFilterApply} style={{ padding: "0 16px", height: 36, borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            <SearchOutlined /> Apply
          </button>
          {(dlSearch || dlUrgency || dlAttorney || dlDateRange[0] || dlDateRange[1]) && (
            <button onClick={async () => { setDlSearch(""); setDlUrgency(""); setDlAttorney(""); setDlDateRange(["", ""]); setDeadlineCatFilter("all"); setDlLoading(true); try { const res = await fetchAllDeadlines(id, {}); setDeadlines(res || {}); } catch {} finally { setDlLoading(false); } }} style={{ padding: "0 14px", height: 36, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
              <i className="bx bx-x" style={{ fontSize: 16 }} /> Clear Filters
            </button>
          )}
          {exportBtn(handleDlExport)}
        </div>

        {/* Table */}
        <div style={cardStyle}>
          <div style={{ padding: "16px 24px" }}>
            <Table
              dataSource={filteredDeadlines}
              columns={deadlineColumns}
              rowKey={(r) => r.id}
              size="small"
              scroll={{ x: 900 }}
              loading={dlLoading}
              pagination={{ pageSize: 20, showSizeChanger: false }}
            />
          </div>
        </div>
      </div>
    );
  };

  // ── Journal Watch Tab ──
  // Selected month key for journal selector
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Auto-select first month when journals load
  const currentMonthKey = useMemo(() => {
    if (journalsByMonth.length > 0) return journalsByMonth[0].label;
    return null;
  }, [journalsByMonth]);

  const activeMonth = selectedMonth || currentMonthKey;

  const activeMonthJournals = useMemo(() => {
    const group = journalsByMonth.find((g) => g.label === activeMonth);
    return group?.journals || [];
  }, [journalsByMonth, activeMonth]);

  const activeMonthFull = useMemo(() => {
    if (!activeMonthJournals.length) return "";
    const d = new Date(activeMonthJournals[0].date_of_publication);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" }).toUpperCase();
  }, [activeMonthJournals]);

  const renderJournalSelector = () => (
    <div style={{ ...cardStyle, padding: "20px 24px", marginBottom: 20, background: "linear-gradient(180deg,#fafbff,#fff)" }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", marginBottom: 12 }}>Select Journal by Month</div>

      {/* Month pills */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {journalsByMonth.map((group) => {
          const isActive = activeMonth === group.label;
          return (
            <button
              key={group.label}
              onClick={() => setSelectedMonth(group.label)}
              style={{
                padding: "6px 16px",
                borderRadius: 20,
                border: isActive ? "2px solid #3b82f6" : "1px solid #e2e8f0",
                background: isActive ? "#eff6ff" : "#fff",
                color: isActive ? "#2563eb" : "#64748b",
                fontWeight: isActive ? 700 : 500,
                fontSize: 13,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {group.label}
            </button>
          );
        })}
      </div>

      {/* Journal code pills for selected month */}
      {activeMonthJournals.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 8 }}>
            JOURNALS — {activeMonthFull}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {activeMonthJournals.map((j) => {
              const d = new Date(j.date_of_publication);
              const dateLabel = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              const isActive = selectedJournal === j.journal_number;
              return (
                <button
                  key={j.journal_number}
                  onClick={() => setSelectedJournal(j.journal_number)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 20,
                    border: isActive ? "2px solid #16a34a" : "1px solid #e2e8f0",
                    background: isActive ? "#f0fdf4" : "#fff",
                    color: isActive ? "#15803d" : "#475569",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 13,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontWeight: 700, color: isActive ? "#15803d" : "#1e293b" }}>{j.journal_number}</span>
                  <span style={{ color: isActive ? "#16a34a" : "#94a3b8" }}> · {dateLabel}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  // ── Normalize match item fields ──
  const normalizeMatch = (item) => {
    const src = item.source || {};
    const tgt = item.target || {};
    const match = item.match_details || {};
    return {
      srcName: src.company_name || src.name || "",
      srcTM: src.company_trade_mark || src.trade_mark || "",
      srcClass: src.company_class || src.class || "",
      srcLogo: src.company_logo_url || src.logo_url || "",
      srcStatus: src.company_status || src.status || "",
      srcProprietor: src.company_proprietor_name || src.proprietor_name || "",
      srcDate: src.company_date_of_application || src.date_of_application || "",
      srcWords: match.focus_words?.your_focus_word || (match.matching_words || []).join(", ") || src.company_clean_name || src.clean_name || "",
      tgtName: tgt.journal_name || tgt.name || "",
      tgtTM: tgt.journal_trade_mark || tgt.trade_mark || "",
      tgtClass: tgt.journal_class || tgt.class || "",
      tgtLogo: tgt.journal_logo_url || tgt.logo_url || "",
      tgtStatus: tgt.journal_status || tgt.status || "",
      tgtProprietor: tgt.journal_proprietor_name || tgt.proprietor_name || "",
      tgtDate: tgt.journal_date_of_application || tgt.date_of_application || "",
      tgtWords: match.focus_words?.threat_focus_word || (match.matching_words || []).join(", ") || tgt.journal_clean_name || tgt.clean_name || "",
      score: match.similarity_score ?? 0,
      matchType: match.match_type || (match.same_class ? "Same Class" : "Cross Class"),
      sameClass: match.same_class ?? ((src.company_class || src.class) === (tgt.journal_class || tgt.class)),
    };
  };

  const fmtMatchDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  /*  PDF Generation removed — using CSV export instead
  const generatePDF = (results, title, journalCode) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    // ── Cover page (dark theme) ──
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, W, H, "F");

    // Accent line
    doc.setFillColor(59, 130, 246);
    doc.rect(20, 40, 40, 4, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(36);
    doc.setFont("helvetica", "bold");
    doc.text(title.toUpperCase(), 20, 65);
    doc.setTextColor(96, 165, 250);
    doc.setFontSize(32);
    doc.text("WATCH REPORT", 20, 80);

    doc.setTextColor(148, 163, 184);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Trademark Conflicts Identified From Published " + (journalCode ? "Journal Entries" : "Recent Filings"), 20, 95);

    // Info cards
    const cardY = 115;
    const cardW = 70;
    const cardH = 30;
    [[20, "USER ID", `#${id}`], [100, "TOTAL MATCHES", `${results.length}`], [180, "GENERATED", new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })]].forEach(([x, label, val]) => {
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(x, cardY, cardW, cardH, 3, 3, "F");
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(8);
      doc.text(label, x + 8, cardY + 10);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(val, x + 8, cardY + 22);
    });

    // Legend
    doc.setFont("helvetica", "normal");
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(9);
    doc.text("SIMILARITY SCORE LEGEND", 20, 165);
    const legends = [
      [20, "#dc2626", "90-100% Critical"],
      [70, "#f59e0b", "70-89% High"],
      [120, "#3b82f6", "50-69% Moderate"],
      [170, "#94a3b8", "<50% Low"],
    ];
    legends.forEach(([x, color, label]) => {
      const rgb = parseInt(color.slice(1), 16);
      doc.setFillColor((rgb >> 16) & 255, (rgb >> 8) & 255, rgb & 255);
      doc.rect(x, 170, 8, 4, "F");
      doc.setTextColor(203, 213, 225);
      doc.setFontSize(9);
      doc.text(label, x + 12, 173);
    });

    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8);
    doc.text(`© ${new Date().getFullYear()} All rights reserved. This report is confidential.`, W / 2, H - 15, { align: "center" });

    // ── Data pages ──
    doc.addPage("a4", "landscape");

    const rows = results.map((item, i) => {
      const m = normalizeMatch(item);
      return [
        i + 1,
        fmtMatchDate(m.srcDate),
        m.srcClass,
        (m.srcProprietor || "").substring(0, 40),
        `${m.srcTM}\n${m.srcName}\n[${m.srcStatus}]`,
        `${m.score}%\n${m.matchType}`,
        `${m.srcWords}  ↔  ${m.tgtWords}`,
        `${m.tgtTM}\n${m.tgtName}\n[${m.tgtStatus}]`,
        (m.tgtProprietor || "").substring(0, 40),
        m.tgtClass,
        fmtMatchDate(m.tgtDate),
      ];
    });

    doc.autoTable({
      head: [[
        { content: "USER'S MARKS", colSpan: 5, styles: { fillColor: [219, 234, 254], textColor: [29, 78, 216], halign: "center", fontStyle: "bold", fontSize: 9 } },
        { content: "MATCH", colSpan: 2, styles: { fillColor: [254, 243, 199], textColor: [146, 64, 14], halign: "center", fontStyle: "bold", fontSize: 9 } },
        { content: "THREAT MARKS", colSpan: 4, styles: { fillColor: [254, 226, 226], textColor: [153, 27, 27], halign: "center", fontStyle: "bold", fontSize: 9 } },
      ], ["#", "App Date", "Class", "Proprietor", "User's TM", "Score", "Focus Words", "Threat TM", "Proprietor", "Class", "App Date"]],
      body: rows,
      startY: 20,
      styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak", lineWidth: 0.1, lineColor: [226, 232, 240] },
      headStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: "bold", fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        2: { cellWidth: 12, halign: "center" },
        5: { cellWidth: 20, halign: "center" },
        9: { cellWidth: 12, halign: "center" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 5) {
          const score = parseInt(data.cell.raw);
          if (score >= 90) data.cell.styles.textColor = [220, 38, 38];
          else if (score >= 70) data.cell.styles.textColor = [245, 158, 11];
          else data.cell.styles.textColor = [34, 197, 94];
          data.cell.styles.fontStyle = "bold";
        }
      },
      margin: { left: 10, right: 10 },
    });

    const fileName = journalCode ? `Journal_Watch_${journalCode}_User_${id}.pdf` : `Pre_Journal_Watch_User_${id}.pdf`;
    doc.save(fileName);
  };  */

  // ── Match table (proper <table> layout like CRM) ──
  const renderMatchTable = (results, isJournal) => {
    if (!results || results.length === 0) return <Empty description="No matches found" style={{ padding: 40 }} />;

    const thStyle = { padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#475569", textAlign: "center", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
    const tdStyle = { padding: "12px 10px", fontSize: 12, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" };

    return (
      <div style={{ borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
            <thead>
              {/* Section headers */}
              <tr>
                <th colSpan={5} style={{ padding: "10px", background: "linear-gradient(135deg,#dcfce7,#f0fdf4)", borderBottom: "3px solid #22c55e", textAlign: "center" }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: "#15803d", textTransform: "uppercase", letterSpacing: 1.5 }}>User's Marks</span>
                </th>
                <th colSpan={2} style={{ padding: "10px", background: "linear-gradient(135deg,#fef3c7,#fffbeb)", borderBottom: "3px solid #f59e0b", textAlign: "center" }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: "#92400e", textTransform: "uppercase", letterSpacing: 1.5 }}>Match Details</span>
                </th>
                <th colSpan={5} style={{ padding: "10px", background: "linear-gradient(135deg,#fee2e2,#fef2f2)", borderBottom: "3px solid #ef4444", textAlign: "center" }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: "#991b1b", textTransform: "uppercase", letterSpacing: 1.5 }}>Threat Marks</span>
                </th>
              </tr>
              {/* Column sub-headers */}
              <tr style={{ background: "#f8fafc" }}>
                <th style={thStyle}>App. Date</th>
                <th style={thStyle}>Class</th>
                <th style={thStyle}>Proprietor</th>
                <th style={{ ...thStyle, minWidth: 160 }}>User's TM No.</th>
                <th style={thStyle}></th>
                <th style={{ ...thStyle, minWidth: 100 }}>Score</th>
                <th style={{ ...thStyle, minWidth: 200 }}>Focus Words</th>
                <th style={{ ...thStyle, minWidth: 160 }}>Threat TM No.</th>
                <th style={thStyle}></th>
                <th style={thStyle}>Proprietor</th>
                <th style={thStyle}>Class</th>
                <th style={thStyle}>App. Date</th>
              </tr>
            </thead>
            <tbody>
              {results.map((item, idx) => {
                const m = normalizeMatch(item);
                const scoreColor = m.score >= 90 ? "#dc2626" : m.score >= 70 ? "#f59e0b" : "#22c55e";
                const scoreBg = m.score >= 90 ? "#fef2f2" : m.score >= 70 ? "#fffbeb" : "#f0fdf4";

                return (
                  <tr key={idx} style={{ transition: "background 0.15s" }} onMouseEnter={(e) => e.currentTarget.style.background = "#fafbff"} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                    {/* User's App Date */}
                    <td style={{ ...tdStyle, textAlign: "center", fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>{fmtMatchDate(m.srcDate)}</td>
                    {/* User's Class */}
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <span style={{ display: "inline-block", minWidth: 28, padding: "2px 8px", borderRadius: 6, background: "#eff6ff", color: "#2563eb", fontWeight: 700, fontSize: 13, textAlign: "center" }}>{m.srcClass}</span>
                    </td>
                    {/* User's Proprietor */}
                    <td style={{ ...tdStyle, maxWidth: 140 }}>
                      <Tooltip title={m.srcProprietor}>
                        <div style={{ fontSize: 11, color: "#1e293b", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "uppercase" }}>{m.srcProprietor}</div>
                      </Tooltip>
                    </td>
                    {/* User's TM */}
                    <td style={tdStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 12, color: "#6366f1", fontWeight: 600 }}>{m.srcTM}</div>
                          <div style={{ fontWeight: 600, fontSize: 12, color: "#1e293b" }}>{m.srcName}</div>
                          {statusTag(m.srcStatus)}
                        </div>
                      </div>
                    </td>
                    {/* User's Logo */}
                    <td style={{ ...tdStyle, textAlign: "center", width: 44 }}>{tmLogo(m.srcLogo, m.srcName)}</td>

                    {/* Score */}
                    <td style={{ ...tdStyle, textAlign: "center", background: "#fffdf7", borderLeft: "2px solid #fde68a" }}>
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 8, background: scoreBg, color: scoreColor, fontWeight: 800, fontSize: 14, border: `1.5px solid ${scoreColor}30` }}>{m.score}%</span>
                      </div>
                      <div style={{ fontSize: 10, color: "#92400e", fontWeight: 600 }}>{m.matchType}</div>
                      {m.sameClass && (
                        <div style={{ marginTop: 4 }}>
                          <span style={{ padding: "2px 8px", borderRadius: 8, background: "#dcfce7", color: "#16a34a", fontSize: 9, fontWeight: 700 }}>Same Class — {m.srcClass}</span>
                        </div>
                      )}
                    </td>
                    {/* Focus Words */}
                    <td style={{ ...tdStyle, background: "#fffdf7", borderRight: "2px solid #fde68a" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                        <div style={{ padding: "4px 10px", borderRadius: 6, background: "#dcfce7", border: "1px solid #bbf7d0", textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: "#16a34a", fontWeight: 600 }}>User's Word</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>{m.srcWords}</div>
                        </div>
                        <span style={{ color: "#94a3b8", fontSize: 16 }}>⇄</span>
                        <div style={{ padding: "4px 10px", borderRadius: 6, background: "#fee2e2", border: "1px solid #fecaca", textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: "#dc2626", fontWeight: 600 }}>Threat Word</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#b91c1c" }}>{m.tgtWords}</div>
                        </div>
                      </div>
                    </td>

                    {/* Threat TM */}
                    <td style={tdStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>{m.tgtTM}</div>
                          <div style={{ fontWeight: 600, fontSize: 12, color: "#1e293b" }}>{m.tgtName}</div>
                          {statusTag(m.tgtStatus)}
                        </div>
                      </div>
                    </td>
                    {/* Threat Logo */}
                    <td style={{ ...tdStyle, textAlign: "center", width: 44 }}>{tmLogo(m.tgtLogo, m.tgtName)}</td>
                    {/* Threat Proprietor */}
                    <td style={{ ...tdStyle, maxWidth: 140 }}>
                      <Tooltip title={m.tgtProprietor}>
                        <div style={{ fontSize: 11, color: "#1e293b", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "uppercase" }}>{m.tgtProprietor}</div>
                      </Tooltip>
                    </td>
                    {/* Threat Class */}
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <span style={{ display: "inline-block", minWidth: 28, padding: "2px 8px", borderRadius: 6, background: "#fef2f2", color: "#dc2626", fontWeight: 700, fontSize: 13, textAlign: "center" }}>{m.tgtClass}</span>
                    </td>
                    {/* Threat App Date */}
                    <td style={{ ...tdStyle, textAlign: "center", fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>{fmtMatchDate(m.tgtDate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Journal Watch Tab (API 7 — live comparison from published journals) ──
  const renderJournal = () => (
    <div>
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 0", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#6366f1,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16 }}><EyeOutlined /></div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Journal Watch</div>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Live trademark conflict comparison from published journals</div>
            </div>
          </div>
          {exportBtn(() => exportCSV((journalResults?.results || []).map((r) => { const m = normalizeMatch(r); return { User_TM: m.srcTM, User_Name: m.srcName, User_Class: m.srcClass, Score: m.score, Match_Type: m.matchType, Threat_TM: m.tgtTM, Threat_Name: m.tgtName, Threat_Class: m.tgtClass, Same_Class: m.sameClass ? "Yes" : "No" }; }), `Journal_Watch_${selectedJournal}_User_${id}.csv`))}
        </div>

        <div style={{ padding: "16px 24px" }}>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 20 }}>
            {/* Left: Journal selector */}
            <div style={{ flex: 1, minWidth: 300 }}>
              {journalCodes.length > 0 ? renderJournalSelector() : <Spin size="small" />}
              {/* Search */}
              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 6 }}>Search Trademark</div>
                <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Search by trademark name..." value={jSearch} onChange={(e) => setJSearch(e.target.value)} onPressEnter={() => searchJournal({ search: jSearch || undefined })} allowClear onClear={() => { setJSearch(""); searchJournal({ search: undefined }); }} style={{ borderRadius: 8, height: 36, maxWidth: 400 }} />
              </div>
            </div>

            {/* Right: Filters */}
            <div style={{ ...cardStyle, flex: 1, minWidth: 300, padding: "16px 20px", background: "linear-gradient(180deg,#fafbff,#fff)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <SearchOutlined style={{ color: "#6366f1" }} />
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>Filters</span>
                </div>
                <span style={{ fontSize: 13, color: "#6366f1", fontWeight: 600 }}>{fmt(journalResults?.total || 0)} matches</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={labelS}>Class Matching</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, height: 36, padding: "0 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff" }}>
                    <button onClick={() => { const v = !jSameClass; setJSameClass(v); searchJournal({ same_class: v || undefined }); }} style={{ width: 36, height: 20, borderRadius: 10, border: "none", background: jSameClass ? "#3b82f6" : "#cbd5e1", position: "relative", cursor: "pointer", transition: "background 0.2s" }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: jSameClass ? 18 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                    </button>
                    <span style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>Same class only</span>
                  </div>
                </div>
                <div>
                  <div style={labelS}>Min Similarity</div>
                  <Select value={jMinSim} onChange={(v) => { setJMinSim(v); searchJournal({ min_similarity: v }); }} style={{ width: "100%", height: 36 }}>
                    <Option value={50}>50%</Option>
                    <Option value={70}>70%</Option>
                    <Option value={80}>80%</Option>
                    <Option value={90}>90%</Option>
                  </Select>
                </div>
              </div>

              <div>
                <div style={labelS}>Match Types</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {["Identical Match", "Full Word Match", "Prefix Match", "Suffix Match", "Full Match", "Low Similarity"].map((type) => {
                    const active = jMatchTypes.includes(type);
                    return (
                      <button key={type} onClick={() => {
                        const next = active ? jMatchTypes.filter((t) => t !== type) : [...jMatchTypes, type];
                        setJMatchTypes(next);
                        searchJournal({ match_types: next.length ? next.join(",") : undefined });
                      }} style={{
                        padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
                        border: active ? "1.5px solid #6366f1" : "1px solid #e2e8f0",
                        background: active ? "#eef2ff" : "#fff",
                        color: active ? "#4f46e5" : "#64748b",
                      }}>
                        {type}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          {journalResults && !journalLoading && (
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { label: "Total Matches", value: journalResults.total || 0, color: "#6366f1", icon: "📊" },
                { label: "90-100% Critical", value: (journalResults.results || []).filter((r) => r.match_details?.similarity_score >= 90).length, color: "#dc2626", icon: "🔴" },
                { label: "70-89% High", value: (journalResults.results || []).filter((r) => { const s = r.match_details?.similarity_score; return s >= 70 && s < 90; }).length, color: "#f59e0b", icon: "🟠" },
                { label: "50-69% Moderate", value: (journalResults.results || []).filter((r) => { const s = r.match_details?.similarity_score; return s >= 50 && s < 70; }).length, color: "#3b82f6", icon: "🔵" },
              ].map((s) => (
                <div key={s.label} style={{ padding: "10px 16px", borderRadius: 12, background: "#fff", border: `1px solid ${s.color}25`, boxShadow: `0 2px 8px ${s.color}10`, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{s.icon}</span>
                  <div>
                    <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontWeight: 800, color: s.color, fontSize: 18, lineHeight: 1 }}>{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {journalLoading ? (
            <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
          ) : (journalResults?.results || []).length > 0 ? (
            <>
              {renderMatchTable((journalResults.results || []).slice((jPage - 1) * 20, jPage * 20), true)}
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 0 8px" }}>
                <Pagination
                  current={jPage}
                  pageSize={20}
                  total={journalResults.results.length}
                  onChange={(p) => setJPage(p)}
                  showSizeChanger={false}
                />
              </div>
            </>
          ) : selectedJournal ? (
            <Empty description="No matches found for this journal" style={{ padding: 40 }} />
          ) : null}
        </div>
      </div>
    </div>
  );

  // ── Pre-Journal Watch Tab (API 8) ──
  const [pjSearch, setPjSearch] = useState("");
  const [pjSameClass, setPjSameClass] = useState(false);
  const [pjMinSim, setPjMinSim] = useState(50);
  const [pjClass, setPjClass] = useState("");
  const [pjPage, setPjPage] = useState(1);

  const pjFilterParams = (overrides = {}) => {
    const f = { same_class: pjSameClass || undefined, min_similarity: pjMinSim || undefined, search_name: pjSearch || undefined, class_filter: pjClass || undefined, ...overrides };
    Object.keys(f).forEach((k) => { if (f[k] === "" || f[k] === undefined || f[k] === null || f[k] === false) delete f[k]; });
    return f;
  };

  const searchPj = (overrides = {}) => {
    setPjPage(1); setPreJournalLoading(true);
    fetchPreJournalData(id, 1, 50, pjFilterParams(overrides))
      .then((res) => setPreJournal(res)).catch(() => setPreJournal(null)).finally(() => setPreJournalLoading(false));
  };

  const resetPj = () => {
    setPjSearch(""); setPjSameClass(false); setPjMinSim(50); setPjClass(""); setPjPage(1);
    setPreJournalLoading(true);
    fetchPreJournalData(id, 1, 50)
      .then((res) => setPreJournal(res)).catch(() => setPreJournal(null)).finally(() => setPreJournalLoading(false));
  };

  const loadPjPage = (page) => {
    setPreJournalLoading(true);
    fetchPreJournalData(id, page, 50, pjFilterParams())
      .then((res) => { setPreJournal(res); setPjPage(page); }).catch(() => setPreJournal(null)).finally(() => setPreJournalLoading(false));
  };

  // Store initial class list from first pre-journal load
  const [pjClassListStored, setPjClassListStored] = useState([]);
  useEffect(() => {
    if (preJournal?.results?.length && pjClassListStored.length === 0) {
      const map = {};
      preJournal.results.forEach((r) => { const c = r.source?.class; if (c) map[c] = (map[c] || 0) + 1; });
      setPjClassListStored(Object.entries(map).map(([cls, count]) => ({ cls, count })).sort((a, b) => Number(a.cls) - Number(b.cls)));
    }
  }, [preJournal?.results]);
  const pjClassList = pjClassListStored;

  const renderPreJournal = () => (
    <div>
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 0", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#f97316,#ea580c)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16 }}><FireOutlined /></div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Pre-Journal Watch</div>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Existing trademark matches against recently filed applications</div>
            </div>
          </div>
          {exportBtn(() => exportCSV((preJournal?.results || []).slice(0, 50).map((r) => { const m = normalizeMatch(r); return { User_TM: m.srcTM, User_Name: m.srcName, User_Class: m.srcClass, Score: m.score, Match_Type: m.matchType, Threat_TM: m.tgtTM, Threat_Name: m.tgtName, Threat_Class: m.tgtClass, Same_Class: m.sameClass ? "Yes" : "No" }; }), `Pre_Journal_Watch_User_${id}.csv`))}
        </div>

        <div style={{ padding: "16px 24px" }}>
          {/* Filters */}
          <div style={{ ...cardStyle, padding: "16px 20px", marginBottom: 16, background: "linear-gradient(180deg,#fafbff,#fff)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <SearchOutlined style={{ color: "#6366f1" }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>Filters & Search</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, color: "#6366f1", fontWeight: 600 }}>{fmt(preJournal?.total || 0)} matches</span>
                {(pjSearch || pjSameClass || pjMinSim !== 50 || pjClass) && (
                  <button onClick={resetPj} style={{ padding: "0 12px", height: 30, borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: 600, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                    <i className="bx bx-reset" style={{ fontSize: 13 }} /> Reset
                  </button>
                )}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, alignItems: "end" }}>
              <div>
                <div style={labelS}>Trademark Name</div>
                <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Search your trademarks..." value={pjSearch} onChange={(e) => setPjSearch(e.target.value)} onPressEnter={() => searchPj({ search_name: pjSearch || undefined })} allowClear onClear={() => { setPjSearch(""); searchPj({ search_name: undefined }); }} style={{ borderRadius: 8, height: 36 }} />
              </div>
              <div>
                <div style={labelS}>Class</div>
                <Select value={pjClass || undefined} placeholder="All classes" allowClear onChange={(v) => { setPjClass(v || ""); searchPj({ class_filter: v || undefined }); }} style={{ width: "100%", height: 36 }} showSearch>
                  {pjClassList.map((c) => <Option key={c.cls} value={c.cls}>{c.cls} ({c.count})</Option>)}
                </Select>
              </div>
              <div>
                <div style={labelS}>Class Matching</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, height: 36, padding: "0 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff" }}>
                  <button onClick={() => { const v = !pjSameClass; setPjSameClass(v); searchPj({ same_class: v || undefined }); }} style={{ width: 36, height: 20, borderRadius: 10, border: "none", background: pjSameClass ? "#3b82f6" : "#cbd5e1", position: "relative", cursor: "pointer", transition: "background 0.2s" }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: pjSameClass ? 18 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                  </button>
                  <span style={{ fontSize: 11, color: "#475569", fontWeight: 500 }}>Same class only</span>
                </div>
              </div>
              <div>
                <div style={labelS}>Min Similarity</div>
                <Select value={pjMinSim} onChange={(v) => { setPjMinSim(v); searchPj({ min_similarity: v }); }} style={{ width: "100%", height: 36 }}>
                  <Option value={50}>50%</Option>
                  <Option value={70}>70%</Option>
                  <Option value={80}>80%</Option>
                  <Option value={90}>90%</Option>
                </Select>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          {preJournal && !preJournalLoading && (
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { label: "Total Matches", value: fmt(preJournal.total || 0), color: "#6366f1", icon: "📊" },
                { label: "Comparisons", value: fmt(preJournal.comparisons_performed || 0), color: "#8b5cf6", icon: "🔄" },
                { label: "User Trademarks", value: fmt(preJournal.user_trademarks_count || 0), color: "#06b6d4", icon: "📋" },
              ].map((s) => (
                <div key={s.label} style={{ padding: "10px 16px", borderRadius: 12, background: "#fff", border: `1px solid ${s.color}25`, boxShadow: `0 2px 8px ${s.color}10`, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{s.icon}</span>
                  <div>
                    <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontWeight: 800, color: s.color, fontSize: 18, lineHeight: 1 }}>{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {preJournalLoading ? (
            <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
          ) : (preJournal?.results || []).length > 0 ? (
            <>
              {renderMatchTable(preJournal.results, false)}
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 0 8px" }}>
                <Pagination
                  current={pjPage}
                  pageSize={50}
                  total={preJournal.total || preJournal.results.length}
                  onChange={(p) => loadPjPage(p)}
                  showSizeChanger={false}
                />
              </div>
            </>
          ) : (
            <Empty description="No pre-journal matches found" style={{ padding: 40 }} />
          )}
        </div>
      </div>
    </div>
  );

  // ─── Renewals ─────────────────────────────────────────────────────────────
  // Uses precomputed daysTo* from renewal_details; dateStr is the ISO date for display
  const renewalBadge = (dateStr, days) => {
    if (dateStr === undefined && days === undefined) return <span style={{ color: "#94a3b8" }}>—</span>;
    const fmt12 = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? s : d.toISOString().slice(0, 10); };
    let color, bg, border, label;
    if (days < 0) {
      color = "#dc2626"; bg = "#fef2f2"; border = "#fecaca";
      label = `${Math.abs(days)} days overdue`;
    } else if (days <= 30) {
      color = "#f97316"; bg = "#fff7ed"; border = "#fed7aa";
      label = `${days} days left`;
    } else if (days <= 90) {
      color = "#d97706"; bg = "#fffbeb"; border = "#fde68a";
      label = `${days} days left`;
    } else {
      color = "#16a34a"; bg = "#f0fdf4"; border = "#bbf7d0";
      label = `${days} days left`;
    }
    return (
      <div>
        <div style={{ fontSize: 12, color: "#374151", marginBottom: 3 }}>{fmt12(dateStr)}</div>
        <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, color, background: bg, border: `1px solid ${border}`, whiteSpace: "nowrap" }}>{label}</span>
      </div>
    );
  };

  const renewalRows = (() => {
    const rows = renewals?.data || [];
    if (!renewalsSearch) return rows;
    const q = renewalsSearch.toLowerCase();
    return rows.filter((r) =>
      r.trade_mark?.toLowerCase().includes(q) ||
      r.name?.toLowerCase().includes(q) ||
      r.proprietor_name?.toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q)
    );
  })();

  const renderRenewals = () => (
    <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>Renewals</div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 2 }}>Upcoming renewal deadlines across your portfolio.</div>
          </div>
          <button
            onClick={() => loadRenewals(1)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            <SyncOutlined spin={renewalsLoading} /> Refresh
          </button>
        </div>
        <Input
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          placeholder="Search TM, wordmark, proprietor, status..."
          value={renewalsSearch}
          onChange={(e) => setRenewalsSearch(e.target.value)}
          style={{ maxWidth: 380, borderRadius: 8 }}
          allowClear
        />
      </div>

      {renewalsLoading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
      ) : renewalRows.length === 0 ? (
        <Empty description="No renewal data found" style={{ padding: 40 }} />
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["APPLICATION", "PROPRIETOR", "STATUS", "APPLICATION DATE", "EXPIRY DATE", "RENEWAL DUE", "RENEWAL EXPIRY", "RESTORATION DEADLINE"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#64748b", whiteSpace: "nowrap", borderBottom: "1px solid #e2e8f0", letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renewalRows.map((r, i) => {
                  const rd = r.renewal_details || {};
                  return (
                    <tr key={r.id || i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#fafbff" }}>
                      <td style={{ padding: "12px 14px", minWidth: 190 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {tmLogo(r.logo_url, r.name || r.trade_mark)}
                          <div>
                            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{r.trade_mark || "—"}</div>
                            <div style={{ color: "#64748b", fontSize: 12, marginTop: 1 }}>{r.name || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "12px 14px", color: "#374151", minWidth: 160, maxWidth: 220, fontSize: 12 }}>{r.proprietor_name || "—"}</td>
                      <td style={{ padding: "12px 14px" }}>{statusTag(r.status)}</td>
                      <td style={{ padding: "12px 14px", color: "#374151", whiteSpace: "nowrap", fontSize: 12 }}>
                        {rd.applicationDate ? new Date(rd.applicationDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>{renewalBadge(rd.expiryDate, rd.daysToExpiry)}</td>
                      <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>{renewalBadge(rd.renewalDueDate, rd.daysToRenewalDue)}</td>
                      <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>{renewalBadge(rd.renewalExpiryDate, rd.daysToRenewalExpiry)}</td>
                      <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>{renewalBadge(rd.restorationDeadline, rd.daysToRestorationDeadline)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination footer */}
          <div style={{ padding: "12px 24px", borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              Showing <strong>{((renewalsPage - 1) * PAGE_LIMIT_RENEWALS) + 1}</strong> to <strong>{Math.min(renewalsPage * PAGE_LIMIT_RENEWALS, renewals?.total || renewalRows.length)}</strong> of <strong>{renewals?.total || renewalRows.length}</strong>
            </span>
            <Pagination
              current={renewalsPage}
              pageSize={PAGE_LIMIT_RENEWALS}
              total={renewals?.total || 0}
              onChange={(p) => loadRenewals(p)}
              showSizeChanger={false}
              size="small"
            />
          </div>
        </>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  const tabItems = [
    { key: "overview", label: <span><AuditOutlined /> Overview</span>, children: loadingOverview ? renderOverviewShimmer() : renderOverview() },
    { key: "filings", label: <span><FileProtectOutlined /> Trademarks {dashboard ? <Badge count={db.total_trademarks} style={{ backgroundColor: "#6366f1", marginLeft: 6 }} overflowCount={9999} /> : null}</span>, children: renderFilings() },
    { key: "inbound", label: <span><AlertOutlined /> Inbound {(inbound?.total || db.inbound_opposition_count) ? <Badge count={inbound?.total || db.inbound_opposition_count} style={{ backgroundColor: "#f97316", marginLeft: 6 }} overflowCount={999} /> : null}</span>, children: renderInbound() },
    { key: "outbound", label: <span><SafetyOutlined /> Outbound {outbound?.data?.total ? <Badge count={outbound.data.total} style={{ backgroundColor: "#8b5cf6", marginLeft: 6 }} overflowCount={999} /> : null}</span>, children: renderOutbound() },
    { key: "hearings", label: <span><CalendarOutlined /> Hearings {hearings.length > 0 ? <Badge count={hearings.length} style={{ backgroundColor: "#10b981", marginLeft: 6 }} /> : null}</span>, children: renderHearings() },
    { key: "deadlines", label: <span><ClockCircleOutlined /> Deadlines {deadlineStats.total > 0 ? <Badge count={deadlineStats.total} style={{ backgroundColor: "#dc2626", marginLeft: 6 }} overflowCount={999} /> : null}</span>, children: renderDeadlines() },
    { key: "journal", label: <span><EyeOutlined /> Journal Watch</span>, children: renderJournal() },
    { key: "prejournal", label: <span><FireOutlined /> Pre-Journal Watch</span>, children: renderPreJournal() },
    { key: "renewals", label: <span><ReloadOutlined /> Renewals {renewals?.total ? <Badge count={renewals.total} style={{ backgroundColor: "#f59e0b", marginLeft: 6 }} overflowCount={9999} /> : null}</span>, children: renderRenewals() },

    /* Every account panel behind one tab. Adding all eight to this bar took it
       to nineteen tabs and antd folded the newest into a "..." menu — the ones
       nobody would find. They also answer a different question from the rest of
       the bar: this is the account, not the portfolio. */
    {
      key: "account",
      label: <span><WalletOutlined /> Account</span>,
      children: (
        <AccountTabs
          userId={id}
          summary={summary}
          active={activeTab === "account"}
          openPanel={accountPanel}
          /* Carried in from whichever screen linked here, with the summary as
             the fallback for a URL opened directly. An empty string is handled
             — the modal then sends to the registered address — but a prefilled
             box is what makes the common case one click. */
          customerEmail={
            location.state?.userEmail
            || summary?.email
            || summary?.customer_email
            || ""
          }
        />
      ),
    },

    { key: "profile", label: <span><UserOutlined /> Profile</span>, children: null },
  ];

  return (
    <div className="main-content" style={{ background: "#f8fafc", minHeight: "100vh" }}>
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ ...cardStyle, padding: "20px 24px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                onClick={() => navigate(-1)}
                style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#64748b", fontSize: 16, cursor: "pointer", transition: "all 0.15s",
                  border: "1px solid #e2e8f0",
                }}
              >
                <ArrowLeftOutlined />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h4 style={{ margin: 0, fontWeight: 800, fontSize: 22, color: "#0f172a", letterSpacing: -0.5 }}>
                    360° Portfolio View
                  </h4>
                  <span style={{ padding: "2px 10px", borderRadius: 20, background: "#f0fdf4", color: "#16a34a", fontSize: 11, fontWeight: 600, border: "1px solid #bbf7d0" }}>Live</span>
                  <button onClick={() => navigate(`/cms/manageUserData/user-profile/${id}`)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 20, border: "1.5px solid #e0e7ff", background: "#f5f3ff", color: "#6366f1", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    <EyeOutlined style={{ fontSize: 12 }} /> View Profile
                  </button>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>
                  Comprehensive trademark intelligence for <strong style={{ color: "#475569" }}>{userName || `User #${id}`}</strong>
                </p>
              </div>
            </div>
            <ol className="breadcrumb m-0" style={{ fontSize: 13 }}>
              <li className="breadcrumb-item"><Link to="/cms/home">CMS</Link></li>
              <li className="breadcrumb-item"><Link to="/cms/manage-customer">Customers</Link></li>
              <li className="breadcrumb-item"><Link to={`/cms/manageUserData/user-profile/${id}`}>Profile</Link></li>
              <li className="breadcrumb-item active">360° View</li>
            </ol>
          </div>

          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={(key) => { if (key === "profile") { navigate(`/cms/manageUserData/user-profile/${id}`); } else { setActiveTab(key); } }}
            items={tabItems}
            size="large"
            style={{ background: "transparent" }}
            tabBarStyle={{ fontWeight: 600, marginBottom: 20 }}
          />

        </div>
      </div>
    </div>
  );
};

export default User360View;
